import { useState } from 'react';
import RichTextEditor from '../../components/RichTextEditor';
import { renderRichText } from '../../utils/richText';
import IconAdd from '../../components/icons/IconAdd';
import { ICON_PALETTE, ICON_COLOR_SWATCHES, NOTIFICATION_ROW_CHILD_TYPE_LIST } from '../../variables/notificationBlockTypes';
import AlignControl from './AlignControl';
import NotificationBlockCard from './NotificationBlockCard';

// Mirrors topicBlockEditors.jsx's shape (Editor + renderPreview pair per
// type, registered in NOTIFICATION_BLOCK_EDITOR_REGISTRY below) - the
// Komunikat builder's own block-card system (NotificationBlockCard.jsx)
// looks these up by block.type exactly like TopicElementCard.jsx does.

export function HeadingBlockEditor({ block, onSave, onCancel }) {
   const [text, setText] = useState(block.text || '');
   const [align, setAlign] = useState(block.align || 'left');
   return (
      <div className='exercise-block-generic-editor'>
         <input className='exercise-block-generic-text' type='text' autoFocus
            placeholder='Заголовок' value={text} onChange={(e) => setText(e.target.value)} />
         <AlignControl value={align} onChange={setAlign} />
         <div className='exercise-block-editor-actions'>
            <button type='button' className='exercise-block-save-btn' onClick={() => onSave({ text, align })}>Зберегти</button>
            <button type='button' className='exercise-block-cancel-btn' onClick={onCancel}>Скасувати</button>
         </div>
      </div>
   );
}
export function renderHeadingPreview(block) {
   if (!block.text) return <span className='exercise-block-empty-hint'>Порожньо - натисніть редагувати</span>;
   return <h2 className='communication-block-heading' style={{ textAlign: block.align || 'left' }}>{block.text}</h2>;
}

export function TextBlockEditor({ block, onSave, onCancel }) {
   const [text, setText] = useState(block.text || '');
   const [align, setAlign] = useState(block.align || 'left');
   return (
      <div className='exercise-block-generic-editor'>
         <RichTextEditor value={text} onChange={setText} rows={5}
            placeholder='Текст. Виділіть слово, щоб оформити (жирний/курсив/підкреслення/закреслення/маркер/список)' />
         <AlignControl value={align} onChange={setAlign} />
         <div className='exercise-block-editor-actions'>
            <button type='button' className='exercise-block-save-btn' onClick={() => onSave({ text, align })}>Зберегти</button>
            <button type='button' className='exercise-block-cancel-btn' onClick={onCancel}>Скасувати</button>
         </div>
      </div>
   );
}
export function renderTextPreview(block) {
   if (!block.text) return <span className='exercise-block-empty-hint'>Порожньо - натисніть редагувати</span>;
   return <div className='exercise-block-text-preview' style={{ textAlign: block.align || 'left' }}>{renderRichText(block.text)}</div>;
}

export function IconBlockEditor({ block, onSave, onCancel }) {
   const [icon, setIcon] = useState(block.icon || 'info');
   const [align, setAlign] = useState(block.align || 'center');
   const [color, setColor] = useState(block.color || '');
   return (
      <div className='exercise-block-generic-editor'>
         <div className='communication-icon-palette'>
            {ICON_PALETTE.map((name) => (
               <span key={name} className={'communication-icon-swatch' + (icon === name ? ' active' : '')}
                  title={name} onClick={() => setIcon(name)}>
                  <span className='material-symbols-outlined'>{name}</span>
               </span>
            ))}
         </div>
         <div className='exercise-block-field'>
            <label className='exercise-block-field-label'>Назва іконки (fonts.google.com/icons)</label>
            <div className='communication-icon-name-row'>
               <input className='exercise-block-generic-text' type='text' value={icon} onChange={(e) => setIcon(e.target.value.trim())} />
               <span className='material-symbols-outlined communication-icon-live-preview' style={{ color: color ? '#' + color : undefined }}>{icon}</span>
            </div>
         </div>
         <div className='exercise-block-field'>
            <label className='exercise-block-field-label'>Колір іконки</label>
            <div className='communication-icon-color-row'>
               {ICON_COLOR_SWATCHES.map((hex) => (
                  <span key={hex} className={'communication-icon-color-swatch' + (color === hex ? ' active' : '')}
                     style={{ background: '#' + hex }} title={'#' + hex} onClick={() => setColor(hex)} />
               ))}
               <input className='communication-icon-color-native' type='color'
                  value={'#' + (color || 'e0a35c')} onChange={(e) => setColor(e.target.value.replace('#', ''))} title='Інший колір' />
               {color ? <span className='communication-icon-color-reset' onClick={() => setColor('')}>Скинути</span> : ''}
            </div>
         </div>
         <AlignControl value={align} onChange={setAlign} />
         <div className='exercise-block-editor-actions'>
            <button type='button' className='exercise-block-save-btn' onClick={() => onSave({ icon, align, color })}>Зберегти</button>
            <button type='button' className='exercise-block-cancel-btn' onClick={onCancel}>Скасувати</button>
         </div>
      </div>
   );
}
export function renderIconPreview(block) {
   if (!block.icon) return <span className='exercise-block-empty-hint'>Порожньо - натисніть редагувати</span>;
   return (
      <div className='communication-block-icon-wrap' style={{ textAlign: block.align || 'center' }}>
         <span className='material-symbols-outlined communication-block-icon' style={{ color: block.color ? '#' + block.color : undefined }}>{block.icon}</span>
      </div>
   );
}

let rowChildKeySeq = 1;
function newRowChildId() { return 'rc' + (rowChildKeySeq++) + '-' + Date.now(); }

// A small horizontal container - holds 2+ of the other block types
// (heading/text/icon, never another row - see NOTIFICATION_ROW_CHILD_TYPE_LIST)
// laid out side by side. Reuses NotificationBlockCard for each child, exactly
// like Builder.jsx's own top-level list - same add/edit/move/delete
// interactions, just scoped to `block.children` instead of the page-level
// `blocks` array. Nothing is saved until this editor's own Save button is
// pressed (mirrors every other block editor's onSave contract).
export function RowBlockEditor({ block, onSave, onCancel }) {
   const [children, setChildren] = useState(block.children || []);
   const [addPickerOpen, setAddPickerOpen] = useState(false);

   const addChild = (childType) => {
      const def = NOTIFICATION_ROW_CHILD_TYPE_LIST.find((t) => t.type === childType);
      setChildren((prev) => [...prev, { id: newRowChildId(), type: childType, ...(def ? def.defaultConfig : {}) }]);
      setAddPickerOpen(false);
   };
   const saveChild = (index, updated) => setChildren((prev) => prev.map((c, i) => (i === index ? updated : c)));
   const moveChild = (index, dir) => {
      setChildren((prev) => {
         const next = [...prev];
         const target = index + dir;
         if (target < 0 || target >= next.length) return prev;
         [next[index], next[target]] = [next[target], next[index]];
         return next;
      });
   };
   const deleteChild = (index) => setChildren((prev) => prev.filter((_, i) => i !== index));

   return (
      <div className='exercise-block-generic-editor'>
         <div className='communication-row-children'>
            {children.length === 0 ? <span className='exercise-block-empty-hint'>Рядок порожній - додайте елементи нижче</span> : ''}
            {children.map((child, i) => (
               <NotificationBlockCard key={child.id} block={child}
                  canMoveUp={i > 0} canMoveDown={i < children.length - 1}
                  onMove={(dir) => moveChild(i, dir)}
                  onSave={(updated) => saveChild(i, updated)}
                  onDelete={() => deleteChild(i)} />
            ))}
         </div>
         <div className='exercise-block-add-trigger' onClick={() => setAddPickerOpen((prev) => !prev)}>
            <IconAdd />
            <span>Додати елемент у рядок</span>
         </div>
         {addPickerOpen ? (
            <div className='exercise-block-type-picker'>
               <div className='exercise-block-type-row'>
                  {NOTIFICATION_ROW_CHILD_TYPE_LIST.map((t) => (
                     <div key={t.type} className='exercise-block-type-chip' style={{ '--accent': t.accent }} onClick={() => addChild(t.type)}>
                        {t.label}
                     </div>
                  ))}
               </div>
            </div>
         ) : ''}
         <div className='exercise-block-editor-actions'>
            <button type='button' className='exercise-block-save-btn' onClick={() => onSave({ children })}>Зберегти</button>
            <button type='button' className='exercise-block-cancel-btn' onClick={onCancel}>Скасувати</button>
         </div>
      </div>
   );
}
export function renderRowPreview(block) {
   const children = block.children || [];
   if (children.length === 0) return <span className='exercise-block-empty-hint'>Порожньо - натисніть редагувати</span>;
   return (
      <div className='communication-block-row'>
         {children.map((child) => {
            const entry = NOTIFICATION_BLOCK_EDITOR_REGISTRY[child.type];
            if (!entry) return null;
            // Icon items shrink to their own content width (an icon stays an
            // icon-sized box, never a stretched half of the row); heading/text
            // items share the remaining space - this is what makes "icon then
            // text" sit tight together instead of each taking a rigid 50%.
            return <div key={child.id} className={'communication-block-row-item communication-block-row-item--' + child.type}>{entry.renderPreview(child)}</div>;
         })}
      </div>
   );
}

export const NOTIFICATION_BLOCK_EDITOR_REGISTRY = {
   heading: { Editor: HeadingBlockEditor, renderPreview: renderHeadingPreview },
   text: { Editor: TextBlockEditor, renderPreview: renderTextPreview },
   icon: { Editor: IconBlockEditor, renderPreview: renderIconPreview },
   row: { Editor: RowBlockEditor, renderPreview: renderRowPreview },
};
